import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { FaCamera, FaSignOutAlt, FaKey, FaTimes, FaEnvelope } from "react-icons/fa";
import Swal from "sweetalert2";
import { apiFetch, mediaUrl } from "./utils/api";
import { useAuth } from "./context/AuthContext";
import { createPortal } from "react-dom";
import "./ProfileHeader.css";

const MAX_FILE_SIZE = 0.5 * 1024 * 1024;
const SUPPORT_EMAIL = "admin@dolluzcorp.com";

/** Employee-only. <Header/> mounts this only when authenticated. */
const ProfileHeader = ({ loggedInEmp, setLoggedInEmp }) => {
    const [open, setOpen] = useState(false);
    const [uploadOpen, setUploadOpen] = useState(false);
    const [file, setFile] = useState(null);
    const [saving, setSaving] = useState(false);

    const fileInputRef = useRef(null);
    const wrapRef = useRef(null);
    const buttonRef = useRef(null);

    const navigate = useNavigate();
    const { logout } = useAuth();

    useEffect(() => {
        if (!open) return;

        const onClick = (e) => {
            if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
        };
        const onKey = (e) => {
            if (e.key === "Escape") {
                setOpen(false);
                buttonRef.current?.focus();
            }
        };

        document.addEventListener("mousedown", onClick);
        document.addEventListener("keydown", onKey);
        return () => {
            document.removeEventListener("mousedown", onClick);
            document.removeEventListener("keydown", onKey);
        };
    }, [open]);

    const avatarStyle = (fallbackSize) => ({
        backgroundColor: loggedInEmp?.emp_profile_img ? "transparent" : loggedInEmp?.profile_color,
        backgroundImage: loggedInEmp?.emp_profile_img
            ? `url(${mediaUrl(loggedInEmp.emp_profile_img)})`
            : undefined,
        fontSize: fallbackSize,
    });

    const copyEmail = () => {
        navigator.clipboard.writeText(SUPPORT_EMAIL).then(
            () => Swal.fire({
                toast: true, position: "top", icon: "success",
                title: "Email copied", showConfirmButton: false, timer: 1400,
            }),
            () => Swal.fire({ icon: "error", title: "Couldn't copy the email" })
        );
    };

    const onFileChange = (e) => {
        const picked = e.target.files[0];
        if (!picked) return;

        if (picked.size > MAX_FILE_SIZE) {
            Swal.fire({ icon: "warning", title: "File too large", text: "Maximum size is 0.5 MB." });
            e.target.value = "";
            setFile(null);
            return;
        }
        setFile(picked);
    };

    const saveImage = async () => {
        if (!file) return;
        setSaving(true);

        const body = new FormData();
        body.append("profile", file);

        try {
            const res = await apiFetch("/api/employee/upload-profile", { method: "POST", body });
            const data = await res.json();
            if (!res.ok) throw new Error(data.error || "Upload failed");

            setLoggedInEmp((prev) => ({ ...prev, emp_profile_img: data.profilePath }));
            setUploadOpen(false);
            setFile(null);
            Swal.fire({
                toast: true, position: "top", icon: "success",
                title: "Profile photo updated", showConfirmButton: false, timer: 1600,
            });
        } catch (err) {
            Swal.fire({ icon: "error", title: "Upload failed", text: err.message });
        } finally {
            setSaving(false);
        }
    };

    const handleLogout = async () => {
        await logout();
        sessionStorage.clear();

        // Log out returns you to the public site, not to a login wall — this is a public
        // website and a signed-out visitor still has plenty to read.
        //
        // localStorage is deliberately NOT cleared: it holds the anonymous visitor_key
        // used for banner voting, and wiping it would hand the same person a fresh
        // identity and let them vote a second time.
        navigate("/");
    };

    return (
        <>
            <div className="profile-wrap" ref={wrapRef}>
                <button
                    ref={buttonRef}
                    type="button"
                    className="profile-avatar"
                    style={avatarStyle("var(--t-sm)")}
                    onClick={() => setOpen((o) => !o)}
                    aria-label={`Account menu for ${loggedInEmp?.emp_first_name || "your account"}`}
                    aria-expanded={open}
                    aria-haspopup="true"
                >
                    {!loggedInEmp?.emp_profile_img && loggedInEmp?.profile_letters}
                </button>

                {open && (
                    <div className="profile-menu">
                        <div className="profile-id">
                            <div className="profile-id-avatar" style={avatarStyle("var(--t-md)")}>
                                {!loggedInEmp?.emp_profile_img && loggedInEmp?.profile_letters}

                                <button
                                    type="button"
                                    className="profile-id-camera"
                                    onClick={() => setUploadOpen(true)}
                                    aria-label="Change profile photo"
                                >
                                    <FaCamera aria-hidden="true" />
                                </button>
                            </div>

                            <div style={{ minWidth: 0 }}>
                                <div className="profile-id-name">
                                    {loggedInEmp?.emp_first_name} {loggedInEmp?.emp_last_name}
                                </div>
                                <span className="profile-id-mail">{loggedInEmp?.emp_mail_id}</span>
                            </div>
                        </div>

                        <button type="button" className="profile-item" onClick={copyEmail}>
                            <FaEnvelope aria-hidden="true" /> {SUPPORT_EMAIL}
                        </button>

                        <button
                            type="button"
                            className="profile-item"
                            onClick={() => navigate("/login?changePassword")}
                        >
                            <FaKey aria-hidden="true" /> Change password
                        </button>

                        <div className="profile-divider" />

                        <button type="button" className="profile-item danger" onClick={handleLogout}>
                            <FaSignOutAlt aria-hidden="true" /> Log out
                        </button>
                    </div>
                )}
            </div>

            {uploadOpen && createPortal(
                <div className="modal-backdrop" role="dialog" aria-modal="true" aria-labelledby="upload-title">
                    <div className="modal">
                        <div className="modal-head">
                            <h2 id="upload-title">Update profile photo</h2>
                            <button
                                type="button"
                                className="bell-dismiss"
                                onClick={() => { setUploadOpen(false); setFile(null); }}
                                aria-label="Close"
                            >
                                <FaTimes aria-hidden="true" />
                            </button>
                        </div>

                        <div className="modal-body">
                            <div className="upload-preview">
                                {file ? (
                                    <img src={URL.createObjectURL(file)} alt="Preview of your new avatar" />
                                ) : (
                                    <div className="upload-placeholder" aria-hidden="true">
                                        <FaCamera />
                                    </div>
                                )}
                            </div>

                            <input
                                ref={fileInputRef}
                                type="file"
                                accept="image/*"
                                className="upload-field"
                                onChange={onFileChange}
                                aria-label="Choose a profile photo"
                            />
                            <p className="upload-hint">JPG or PNG, up to 0.5 MB.</p>
                        </div>

                        <div className="modal-actions">
                            <button
                                type="button"
                                className="btn btn-secondary"
                                onClick={() => { setUploadOpen(false); setFile(null); }}
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                className="btn btn-primary"
                                onClick={saveImage}
                                disabled={!file || saving}
                            >
                                {saving ? "Saving…" : "Save photo"}
                            </button>
                        </div>
                    </div>
                </div>,
                document.body
            )}
        </>
    );
};

export default ProfileHeader;
